<script setup lang="ts">
  import { IValidation } from "@/view-models/form.model"
  import ErrorMessage from "./ErrorMessage.vue"

  const modelValue = defineModel<string>({
    default: "",
  })

  const props = withDefaults(
    defineProps<{
      label?: string
      info?: string
      id: string
      validation?: IValidation
      placeholder?: string
      isOptional?: boolean
      isDisabled?: boolean
      isUppercase?: boolean
      isLoading?: boolean
      inputProps?: object
    }>(),
    {
      id: createUuid(),
      label: "",
      info: "",
      validation: undefined,
      placeholder: "",
      isOptional: false,
      isDisabled: false,
      isUppercase: false,
      isLoading: false,
      inputProps: undefined
    }
  )

  const validationFails = computed(() => {
    return props.validation?.model || props.validation?.invalid?.some((validation) => validation.value === true)
  })

  const isInvalid = computed(() => {
    if (validationFails.value && props.validation?.submitted) {
      return true
    }
    return false
  })
</script>

<template>
  <div class="my-4 themed-text-input">
    <slot
      name="label"
      v-bind="{
        isInvalid,
      }"
    >
      <label
        v-if="label"
        :class="{ 'text-danger': isInvalid }"
        class="themed-text-input__label font-bold"
        :for="id"
      >
        {{ label }}
        <span
          v-if="isOptional"
          class="form-label__optional"
        >
          (optioneel)
        </span>
      </label>
    </slot>
    <slot
      name="info"
      v-bind="{
        isInvalid,
      }"
    >
      <p
        v-if="info"
        :class="{ 'text-danger': isInvalid }"
        class="form-info-text"
        v-text="info"
      ></p>
    </slot>
    <slot
      name="input"
      v-bind="{ isInvalid }"
    >
      <div class="d-flex align-items-center">
        <input
          :id="id"
          v-model="modelValue"
          :class="{ 'is-invalid': isInvalid, 'text-uppercase': isUppercase }"
          class="themed-text-input__input label--lg border-none w-full"
          type="text"
          :placeholder="placeholder"
          :disabled="isDisabled"
          v-bind="inputProps"
        />
        <div
          v-if="isLoading"
          class="spinner-border text-primary ms-3 mt-1"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </slot>

    <slot
      name="error"
      v-bind="{
        isInvalid,
      }"
    >
      <error-message
        v-if="validation?.model && validation?.submitted && modelValue === ''"
        :message="validation?.message"
      />
      <template v-if="validation?.invalid && validation?.submitted && modelValue !== ''">
        <div
          v-for="(validationItem, i) in validation?.invalid"
          :key="i"
        >
          <error-message
            v-if="validationItem.value"
            :message="validationItem.message"
          />
        </div>
      </template>
    </slot>
  </div>
</template>
